import { useEffect, useRef, useState } from "react";

type Props = {
  siteKey: string;
  action: "ai-session" | "contact" | "schedule";
  theme: "light" | "dark";
  onToken: (token: string) => void;
  resetKey?: number;
};

export function TurnstileWidget({ siteKey, action, theme, onToken, resetKey = 0 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    onToken("");
    setStatus("loading");
    let attempts = 0;
    const mount = () => {
      if (cancelled || !containerRef.current) return;
      if (!window.turnstile) {
        attempts += 1;
        if (attempts < 50) window.setTimeout(mount, 100);
        else setStatus("error");
        return;
      }
      widgetRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme,
        size: "flexible",
        callback: (token) => {
          setStatus("ready");
          onToken(token);
        },
        "expired-callback": () => onToken(""),
        "error-callback": () => { onToken(""); setStatus("error"); },
        "timeout-callback": () => onToken(""),
      });
      setStatus("ready");
    };
    mount();
    return () => {
      cancelled = true;
      if (widgetRef.current && window.turnstile) window.turnstile.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [action, onToken, resetKey, siteKey, theme]);

  return (
    <div className="turnstile-wrap" data-action="turnstile-spin-v1">
      <div ref={containerRef} />
      {status === "loading" && <span className="microcopy">Loading secure challenge…</span>}
      {status === "error" && <span className="form-error">The security check could not load. Please refresh and try again.</span>}
    </div>
  );
}
